import moment from 'moment'
import React, { useMemo, useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { groupBy, isEmpty, flatten, uniq } from 'lodash'

import { chartColors } from '@/utils/constants'
import WidgetContainer from '@/components/widget/WidgetContainer'
import LineChart from '@/components/charts/LineChart'
import WidgetLineChartTooltip from '@/components/widget/WidgetLineChartTooltip'
import WidgetContext from '@/components/widget/widget.context'
import { getDateFormat } from '@/utils/helpers'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'

export const BaseCountByDateAndEntityWidget = ({
  dataSourceKey,
  entityIdField,
  getEntityName,
  entityColumnLabel,
  entityTooltipLabel,
}) => {
  const { data, loading } = useSelector((state) => state.analytics)
  const {
    groupByValue,
    hiddenItems,
    handleChangeCsvData,
    handleResetWidgetFilters,
    ignoreWeekends,
  } = useContext(WidgetContext)

  const formattedData = useMemo(() => {
    const dataGroupedByEntityId = groupBy(data[dataSourceKey], entityIdField)
    const entityDataGroupedByDateAndSorted = Object.entries(dataGroupedByEntityId)
      .map(([entityId, dataPoints], entityIndex) => {
        const entityName = getEntityName(dataPoints)
        const chartColor = chartColors[entityIndex % chartColors.length]
        const dataPointsGroupedByDate = Object.entries(
          groupBy(dataPoints, (result) => moment.utc(result.date).startOf(groupByValue))
        )
          .sort((a, b) => new Date(a[0]) - new Date(b[0]))
          .filter(([date]) =>
            ignoreWeekends && groupByValue === 'day'
              ? !(moment.utc(date).isoWeekday() === 6 || moment.utc(date).isoWeekday() === 7)
              : true
          )
          .map(([date, dateData]) => {
            return {
              x: getDateFormat(new Date(date), groupByValue),
              y: dateData.reduce((acc, result) => acc + result.count, 0),
              label: entityName,
              id: entityId,
              color: chartColor,
            }
          })
        const item = {
          id: entityId,
          label: entityName,
          data: dataPointsGroupedByDate,
          color: chartColor,
        }
        return item
      })
      .sort((a, b) => {
        return a.label.localeCompare(b.label)
      })
    return entityDataGroupedByDateAndSorted
  }, [data[dataSourceKey], groupByValue, ignoreWeekends])

  const filteredData = useMemo(() => {
    return formattedData.filter((item) => !hiddenItems.includes(item.id))
  }, [formattedData, hiddenItems])

  const tableData = useMemo(() => {
    return flatten(
      filteredData.map((arr) => {
        return arr.data.map((item) => {
          return { ...item, color: arr.color }
        })
      })
    )
  }, [filteredData])

  const tickValues = useMemo(
    () =>
      uniq(tableData.map((dataItem) => dataItem.x)).filter((item, index) => index % 2 === 0) || [],
    [tableData]
  )

  const colors = useMemo(() => filteredData.map((dataItem) => dataItem.color), [filteredData])
  const columns = [
    { accessor: 'x', label: 'Date' },
    { accessor: 'label', label: entityColumnLabel },
    { accessor: 'y', label: 'Response Count', format: (value) => value.toLocaleString() },
  ]
  const groupedByDate = groupBy(tableData, (item) => item.x)
  useEffect(() => {
    handleChangeCsvData({ headers: columns, data: tableData })
  }, [tableData])

  useEffect(() => {
    handleResetWidgetFilters()
  }, [data])

  return (
    <WidgetContainer
      loading={loading.deckPage}
      noData={isEmpty(data[dataSourceKey]) && isEmpty(hiddenItems)}
      withLegend
      dataForLegend={{ data: formattedData }}
      legendAccessor="id"
      table={
        <AdvancedTable
          index="uuid"
          rows={tableData}
          columns={columns}
          pagination
          rowsPerPage={10}
          enableUpdateRowsPerPage={false}
        />
      }
      chart={
        <LineChart
          data={filteredData}
          colors={colors}
          tooltip={({ point }) => {
            const xGroupedData = groupedByDate[point.data.x]
            const tooltipData = xGroupedData.filter((item) => item.y === point.data.y)
            const tooltipItemCount = tooltipData.length

            const noItemsUsed = tooltipItemCount === filteredData.length && point.data.y === 0
            const excessItems = tooltipItemCount > 20 ? tooltipData.length - 19 : 0

            let displayData
            // If none of possible items were used, display text instead of all zeroes
            if (noItemsUsed) {
              displayData = [{ label: `No responses on ${point.data.x}`, displayText: true }]
            } else if (excessItems > 0) {
              // If more than 20 items, cut off the list at 19 & include explanatory text
              displayData = tooltipData.slice(0, 19).concat({
                label: `+ ${excessItems} ${entityTooltipLabel} not displayed`,
                displayText: true,
              })
            } else {
              displayData = [...tooltipData]
            }
            return (
              <WidgetLineChartTooltip
                header={point.data.x}
                tooltipData={displayData}
                keyAccessor="label"
                dataAccessor="y"
              />
            )
          }}
          axisBottom={{
            orient: 'bottom',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Date',
            legendOffset: 36,
            legendPosition: 'middle',
            ...(groupByValue === 'day' &&
              uniq(tableData.map((dataItem) => dataItem.x)).length > 30 && {
                tickValues,
              }),
          }}
          axisLeft={{
            orient: 'left',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Count',
            legendOffset: -60,
            legendPosition: 'middle',
            format: (value) =>
              Math.floor(value) === value ? Number(value).toLocaleString() : value,
          }}
        />
      }
    />
  )
}
